import { WAWI_API, WAWI_SHOP_FILIALE, type Me, type WawiArtikel, type WawiArtikelListe, type WawiStand, type WawiSync, type WawiUebersicht, type WawiWarengruppen } from '@portal/contracts';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { HttpError, wawiApi } from '../../api.js';
import { navigate, onLinkClick, useSearch } from '../../router.js';
import { Problem } from '../ModulePage.js';
import {
  abweichungText, aktiverReiter, artikelPfad, BESTAND_LABEL, bestandIn, darfVerwalten, exportPfad, filialKurz, filialLabel, filialSpalten, filterAusQuery, filterZuQuery, SHOP_LABEL, SORT_LABEL,
  artikelQuery, PHASE_LABEL, UNTERNAVIGATION, WAWI_PFAD, warengruppeAnzeige, warengruppeFilterPfad, wawiRoute, zahl, type WawiFilter, type WawiRoute,
} from './wawi-ui.js';
import './warenwirtschaft.css';

type Laden<T> = { kind: 'laden' } | { kind: 'fehler'; status: number } | { kind: 'ok'; daten: T };
const ZEIT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin' });
const DATUM = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeZone: 'Europe/Berlin' });
const zeit = (s: string | null) => (s ? ZEIT.format(new Date(s)) : '—');
const datum = (s: string | null) => (s ? DATUM.format(new Date(`${s}T12:00:00`)) : '—');
const preis = (n: number | null) => (n === null ? '—' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }));

function useLaden<T>(fn: () => Promise<T>, deps: unknown[]): Laden<T> {
  const [state, setState] = useState<Laden<T>>({ kind: 'laden' });
  useEffect(() => {
    let alive = true;
    setState({ kind: 'laden' });
    fn().then(
      (daten) => alive && setState({ kind: 'ok', daten }),
      (err: unknown) => alive && setState({ kind: 'fehler', status: err instanceof HttpError ? err.status : 0 }),
    );
    return () => {
      alive = false;
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

/**
 * Warenwirtschaft — Team-Sicht der NEO-Quellprojektion (#863, ADR 007): Unternavigation Artikel | Warengruppen | Sync.
 * #899b: komplettes NEO-Sortiment (inkl. Bestand 0) mit Shopify-Spiegel und Abweichung; Filialspalten nur für Filialen mit realem
 * Bestand, Altlasten/Sonderfiliale als Hinweis; Prüflisten als Kacheln. Minusbestand rot. Mobil: Kurzliste (Artikel, F1, Shop, Abw.).
 */
export function Warenwirtschaft({ me, pathname }: { me: Me; pathname: string }) {
  const route = wawiRoute(pathname);
  if (route.seite === 'unbekannt') return <Problem status={404} anrede="sie" />;
  const verwalten = darfVerwalten(me);
  return (
    <section className="mod wawi">
      <h1 className="ap-title">Warenwirtschaft</h1>
      <Reiter route={route} sync={verwalten} />
      {route.seite === 'artikel' && <Artikel />}
      {route.seite === 'detail' && <Detail key={route.nr} nr={route.nr} />}
      {route.seite === 'warengruppen' && <Warengruppen />}
      {route.seite === 'sync' && (verwalten ? <Sync /> : <Problem status={403} anrede="sie" />)}
    </section>
  );
}

function Reiter({ route, sync }: { route: WawiRoute; sync: boolean }) {
  const aktiv = aktiverReiter(route);
  return (
    <nav className="wawi-reiter" aria-label="Warenwirtschaft – Bereiche">
      {UNTERNAVIGATION.filter((u) => u.seite !== 'sync' || sync).map((u) => (
        <a key={u.seite} href={u.pfad} className="wawi-reiter__link" aria-current={aktiv === u.seite ? 'page' : undefined} onClick={onLinkClick(u.pfad)}>
          {u.label}
        </a>
      ))}
    </nav>
  );
}

function Stand({ stand }: { stand: WawiStand }) {
  return (
    <p className={`wawi-stand${stand.veraltet ? ' wawi-stand--alt' : ''}`} role="status">
      Bestand laut NEO vom {zeit(stand.snapshotAt)}
      {stand.veraltet && <span className="wawi-marke wawi-marke--warn">veraltet</span>}
    </p>
  );
}

function Fehler({ status }: { status: number }) {
  if (status === 403 || status === 404) return <Problem status={status} anrede="sie" />;
  return <p className="wawi-hinweis" role="alert">{status === 503 ? 'Die Warenwirtschaft ist noch nicht eingerichtet.' : 'Die Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.'}</p>;
}

function BestandZelle({ text, negativ }: { text: string; negativ: boolean }) {
  return <span className={negativ ? 'wawi-minus' : undefined}>{text}{negativ && <span className="wawi-sr"> (Minusbestand)</span>}</span>;
}

function Artikel() {
  const search = useSearch();
  const filter = filterAusQuery(search);
  const [suche, setSuche] = useState(filter.suche);
  const id = useId();
  useEffect(() => setSuche(filter.suche), [filter.suche]);
  const liste = useLaden<WawiArtikelListe>(() => wawiApi.artikel(artikelQuery(filter)), [search]);
  const kopf = useLaden<WawiUebersicht>(() => wawiApi.uebersicht(), []);
  const setze = (f: Partial<WawiFilter>) => navigate(`${WAWI_PFAD}${filterZuQuery({ ...filter, seite: 1, ...f })}`);
  const absenden = (e: FormEvent) => {
    e.preventDefault();
    setze({ suche });
  };
  const spalten = liste.kind === 'ok' ? filialSpalten(liste.daten.filialen) : [];
  const fName = (nr: number) => (kopf.kind === 'ok' ? kopf.daten.filialen.find((f) => f.nr === nr)?.name : null) ?? filialLabel(nr);
  return (
    <div className="wawi-seite">
      {kopf.kind === 'ok' && (
        <>
          <ul className="wawi-kacheln" aria-label="Prüflisten">
            {kopf.daten.pruefung.map((k) => (
              <li key={k.key}>
                <button type="button" className="wawi-kachel" aria-pressed={filter.pruefliste === k.key} onClick={() => setze({ pruefliste: filter.pruefliste === k.key ? '' : k.key })}>
                  <span className="wawi-kachel__wert">{zahl(k.anzahl)}</span>
                  <span className="wawi-kachel__label">{k.label}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="wawi-hinweis">
            {zahl(kopf.daten.artikel)} Artikel im Sortiment (NEO){kopf.daten.stammFehlt ? ` · ${zahl(kopf.daten.stammFehlt)} ohne Stammsatz (folgt)` : ''}
            {' '}· Shopify: {zahl(kopf.daten.shop.mitNeoSku)} Varianten mit NEO-SKU{kopf.daten.shop.ohneNeoSku ? `, ${zahl(kopf.daten.shop.ohneNeoSku)} ohne` : ''}, Stand {zeit(kopf.daten.shop.lastSuccessAt)}
          </p>
          {kopf.daten.hinweisFilialen.length > 0 && (
            <details className="pb-card wawi-altlast">
              <summary>Altlasten / Sonderfiliale ({kopf.daten.hinweisFilialen.length})</summary>
              <ul>
                {kopf.daten.hinweisFilialen.map((f) => (
                  <li key={f.nr}>
                    {f.name ?? filialLabel(f.nr)} ({f.art === 'sonder' ? 'Sonderfiliale' : 'kein realer Bestand'}): {zahl(f.zeilen)} Zeilen, {zahl(f.positiv)} &gt; 0, {zahl(f.negativ)} &lt; 0, Σ {zahl(f.summe)}{' '}
                    <a href={`${WAWI_PFAD}${filterZuQuery({ ...filter, seite: 1, filiale: String(f.nr), bestand: 'ungleich0' })}`} onClick={onLinkClick(`${WAWI_PFAD}${filterZuQuery({ ...filter, seite: 1, filiale: String(f.nr), bestand: 'ungleich0' })}`)}>anzeigen</a>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
      <form className="wawi-filter" role="search" onSubmit={absenden}>
        <label className="wawi-filter__feld wawi-filter__feld--suche">
          <span className="wawi-filter__label">Suche</span>
          <input id={`${id}-suche`} className="pb-input" type="search" value={suche} maxLength={100} placeholder="ArtikelNr, GTIN, Bezeichnung, Marke" onChange={(e) => setSuche(e.target.value)} />
        </label>
        <label className="wawi-filter__feld">
          <span className="wawi-filter__label">Bestand</span>
          <select className="pb-input" value={filter.bestand} onChange={(e) => setze({ bestand: e.target.value as WawiFilter['bestand'] })}>
            {(Object.keys(BESTAND_LABEL) as WawiFilter['bestand'][]).map((b) => <option key={b} value={b}>{BESTAND_LABEL[b]}</option>)}
          </select>
        </label>
        <label className="wawi-filter__feld">
          <span className="wawi-filter__label">Shopify</span>
          <select className="pb-input" value={filter.shop} onChange={(e) => setze({ shop: e.target.value as WawiFilter['shop'] })}>
            <option value="">Alle</option>
            {(Object.keys(SHOP_LABEL) as (keyof typeof SHOP_LABEL)[]).map((k) => <option key={k} value={k}>{SHOP_LABEL[k]}</option>)}
          </select>
        </label>
        <label className="wawi-filter__feld">
          <span className="wawi-filter__label">Sortierung</span>
          <select className="pb-input" value={`${filter.sort}:${filter.ab ? 'ab' : 'auf'}`} onChange={(e) => { const [sort, r] = e.target.value.split(':'); setze({ sort: sort as WawiFilter['sort'], ab: r === 'ab' }); }}>
            {(Object.keys(SORT_LABEL) as WawiFilter['sort'][]).flatMap((k) => [
              <option key={`${k}:auf`} value={`${k}:auf`}>{SORT_LABEL[k]} ↑</option>,
              <option key={`${k}:ab`} value={`${k}:ab`}>{SORT_LABEL[k]} ↓</option>,
            ])}
          </select>
        </label>
        <button className="pb-button" type="submit">Suchen</button>
        <a className="pb-button pb-button--quiet" href={exportPfad(WAWI_API, filter)} download>CSV-Export</a>
        {(filter.ober || filter.wg || filter.ohneZuordnung || filter.marke || filter.status || filter.filiale || filter.pruefliste) && (
          <button className="pb-button pb-button--quiet" type="button" onClick={() => setze({ ober: '', wg: '', ohneZuordnung: false, marke: '', status: '', filiale: '', pruefliste: '' })}>Filter aufheben</button>
        )}
      </form>
      {liste.kind === 'laden' && <p className="wawi-hinweis" role="status">Lädt …</p>}
      {liste.kind === 'fehler' && <Fehler status={liste.status} />}
      {liste.kind === 'ok' && (
        <>
          <Stand stand={liste.daten.stand} />
          <div className="pb-card wawi-tabelle-wrap">
            <table className="wawi-tabelle wawi-tabelle--sortiment">
              <caption className="wawi-caption">{zahl(liste.daten.gesamt)} Artikel · {BESTAND_LABEL[filter.bestand]}{filter.pruefliste && kopf.kind === 'ok' ? ` · ${kopf.daten.pruefung.find((k) => k.key === filter.pruefliste)?.label}` : ''}{filter.shop ? ` · Shopify ${SHOP_LABEL[filter.shop]}` : ''}{filter.marke ? ` · Marke ${filter.marke}` : ''}{filter.filiale ? ` · ${filialLabel(Number(filter.filiale))}` : ''}{filter.ohneZuordnung ? ' · Nicht zugeordnet' : filter.ober ? ` · Warengruppe ${filter.ober}${filter.wg ? ` › ${filter.wg}` : ''}` : ''}</caption>
              <thead>
                <tr>
                  <th scope="col">Artikel-Nr</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col" className="wawi-lang">Marke</th>
                  <th scope="col" className="wawi-lang">GTIN</th>
                  <th scope="col" className="wawi-zahl wawi-lang">VK</th>
                  <th scope="col" className="wawi-lang">Status NEO</th>
                  {spalten.map((f) => <th key={f} scope="col" className={`wawi-zahl${f === WAWI_SHOP_FILIALE ? '' : ' wawi-lang'}`} title={fName(f)}>{filialKurz(f)}</th>)}
                  <th scope="col" className="wawi-zahl wawi-lang">Res.</th>
                  <th scope="col" className="wawi-zahl">Shopify</th>
                  <th scope="col" className="wawi-lang">Shop-Status</th>
                  <th scope="col" className="wawi-zahl">Abw.</th>
                  <th scope="col" className="wawi-lang">Geändert</th>
                </tr>
              </thead>
              <tbody>
                {liste.daten.eintraege.length === 0 && (
                  <tr className="wawi-leer"><td colSpan={11 + spalten.length}>Keine Artikel für diese Auswahl.</td></tr>
                )}
                {liste.daten.eintraege.map((a) => {
                  const abw = abweichungText(a.abweichung);
                  return (
                    <tr key={a.artikelNr}>
                      <td data-label="Artikel-Nr"><a href={artikelPfad(a.artikelNr)} onClick={onLinkClick(artikelPfad(a.artikelNr))}>{a.artikelNr}</a></td>
                      <td data-label="Bezeichnung">
                        <span className="wawi-name">{a.bezeichnung ?? (a.stammFehlt ? 'Stammsatz folgt' : '—')}</span>
                        {a.keinBestand && <span className="wawi-marke">kein Bestand</span>}
                      </td>
                      <td data-label="Marke" className="wawi-lang">{a.marke ? <button type="button" className="wawi-link" onClick={() => setze({ marke: a.marke! })}>{a.marke}</button> : '—'}</td>
                      <td data-label="GTIN" className="wawi-lang">{a.gtin ?? '—'}</td>
                      <td data-label="VK" className="wawi-zahl wawi-lang">{preis(a.vkPreis)}</td>
                      <td data-label="Status NEO" className="wawi-lang">{a.artikelStatus ?? '—'}</td>
                      {spalten.map((f) => (
                        <td key={f} data-label={fName(f)} className={`wawi-zahl${f === WAWI_SHOP_FILIALE ? '' : ' wawi-lang'}`}><BestandZelle {...bestandIn(a.bestaende, f)} /></td>
                      ))}
                      <td data-label="Reserviert" className="wawi-zahl wawi-lang">{zahl(a.reserviert)}</td>
                      <td data-label="Shopify" className="wawi-zahl">{a.shopBestand === null ? '—' : zahl(a.shopBestand)}</td>
                      <td data-label="Shop-Status" className="wawi-lang"><span className={`wawi-marke${a.shopStatus === 'fehlt' ? ' wawi-marke--warn' : ''}`}>{SHOP_LABEL[a.shopStatus]}</span></td>
                      <td data-label="Abweichung" className={`wawi-zahl${abw.auffaellig ? ' wawi-minus' : ''}`}>{abw.text}</td>
                      <td data-label="Geändert" className="wawi-lang">{zeit(a.geaendertAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Blaettern seite={liste.daten.seite} groesse={liste.daten.groesse} gesamt={liste.daten.gesamt} weiter={(seite) => navigate(`${WAWI_PFAD}${filterZuQuery({ ...filter, seite })}`)} />
          </div>
          <p className="wawi-hinweis">Bestand laut NEO (führend), Reservierungen bereits abgezogen. Abweichung = Bestand F{WAWI_SHOP_FILIALE} − Shopify. Filialen ohne realen Bestand stehen unter „Altlasten / Sonderfiliale“.</p>
        </>
      )}
    </div>
  );
}

function Blaettern({ seite, groesse, gesamt, weiter }: { seite: number; groesse: number; gesamt: number; weiter: (s: number) => void }) {
  const seiten = Math.max(1, Math.ceil(gesamt / groesse));
  if (seiten <= 1) return null;
  return (
    <nav className="wawi-fuss" aria-label="Seiten">
      <button className="pb-button pb-button--quiet" type="button" disabled={seite <= 1} onClick={() => weiter(seite - 1)}>Zurück</button>
      <span>Seite {seite} von {seiten}</span>
      <button className="pb-button pb-button--quiet" type="button" disabled={seite >= seiten} onClick={() => weiter(seite + 1)}>Weiter</button>
    </nav>
  );
}

function Detail({ nr }: { nr: number }) {
  const a = useLaden<WawiArtikel>(() => wawiApi.detail(nr), [nr]);
  if (a.kind === 'laden') return <p className="wawi-hinweis" role="status">Lädt …</p>;
  if (a.kind === 'fehler') return <Fehler status={a.status} />;
  const d = a.daten;
  return (
    <div className="wawi-seite">
      <a className="wawi-zurueck" href={WAWI_PFAD} onClick={onLinkClick(WAWI_PFAD)}>← Artikel</a>
      <div className="pb-card wawi-karte">
        <h2 className="wawi-karte__titel">
          {d.bezeichnung ?? `Artikel ${d.artikelNr}`}
          {d.keinBestand && <span className="wawi-marke">kein Bestand{d.keinBestandSeit ? ` seit ${zeit(d.keinBestandSeit)}` : ''}</span>}
        </h2>
        <dl className="wawi-daten">
          <dt>ArtikelNr</dt><dd>{d.artikelNr}</dd>
          <dt>GTIN</dt><dd>{d.gtin ?? '—'}</dd>
          <dt>Marke</dt><dd>{[d.marke, d.submarke, d.linie].filter(Boolean).join(' · ') || '—'}</dd>
          <dt>Warengruppe</dt><dd>{warengruppeAnzeige(d)}</dd>
          <dt>Kategorie</dt><dd>{d.kategorie ?? '—'}</dd>
          <dt>Lieferant</dt><dd>{d.lieferant ?? '—'}</dd>
          <dt>Status / Typ</dt><dd>{[d.artikelStatus, d.artikeltyp].filter(Boolean).join(' · ') || '—'}</dd>
          <dt>VK / UVP / EK</dt><dd>{preis(d.vkPreis)} / {preis(d.uvpPreis)} / {preis(d.ekPreis)} <span className="wawi-leise">(Währung/Steuerbasis laut NEO nicht belegt)</span></dd>
          <dt>Erstlistung / EX</dt><dd>{datum(d.erstlistung)} / {datum(d.exDatum)}</dd>
          {d.keineBestandsfuehrung && (<><dt>Hinweis</dt><dd><span className="wawi-marke wawi-marke--warn">keine Bestandsführung in NEO</span></dd></>)}
        </dl>
      </div>
      <Stand stand={d.stand} />
      <div className="pb-card wawi-tabelle-wrap">
        <table className="wawi-tabelle">
          <caption className="wawi-caption">Bestand je Filiale</caption>
          <thead>
            <tr>
              <th scope="col">Filiale</th>
              <th scope="col" className="wawi-zahl">Bestand</th>
              <th scope="col" className="wawi-zahl">Reserviert</th>
              <th scope="col" className="wawi-zahl">Avisiert</th>
              <th scope="col" className="wawi-zahl">Meldemenge</th>
              <th scope="col" className="wawi-zahl">Im Warenausgang</th>
            </tr>
          </thead>
          <tbody>
            {d.bestaende.length === 0 && <tr className="wawi-leer"><td colSpan={6}>Keine Bestandszeilen.</td></tr>}
            {d.bestaende.map((b) => (
              <tr key={b.filialNr}>
                <td data-label="Filiale">{filialLabel(b.filialNr)}{b.sonderfiliale && <span className="wawi-marke">Sonderfiliale</span>}</td>
                <td data-label="Bestand" className="wawi-zahl"><BestandZelle text={zahl(b.bestand)} negativ={b.bestand < 0} /></td>
                <td data-label="Reserviert" className="wawi-zahl">{zahl(b.reserviert)}</td>
                <td data-label="Avisiert" className="wawi-zahl">{zahl(b.avisiert)}</td>
                <td data-label="Meldemenge" className="wawi-zahl">{zahl(b.meldemenge)}</td>
                <td data-label="Im Warenausgang" className="wawi-zahl">{zahl(b.ausgang + b.ausgangBestellfiliale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Warengruppen() {
  const w = useLaden<WawiWarengruppen>(() => wawiApi.warengruppen(), []);
  if (w.kind === 'laden') return <p className="wawi-hinweis" role="status">Lädt …</p>;
  if (w.kind === 'fehler') return <Fehler status={w.status} />;
  const spalten = filialSpalten(w.daten.filialen);
  const zuArtikel = (ober: string | null, wg: string | null) => navigate(warengruppeFilterPfad(ober, wg));
  return (
    <div className="wawi-seite">
      <Stand stand={w.daten.stand} />
      {w.daten.baum.length === 0 && <p className="wawi-hinweis">Noch keine Artikel übernommen.</p>}
      {w.daten.baum.map((o) => (
        <details key={o.oberNr ?? '-'} className="pb-card wawi-gruppe" open={w.daten.baum.length <= 3}>
          <summary className="wawi-gruppe__kopf">
            <span className="wawi-name">{o.oberNr ? `${o.oberNr} · ${o.oberName ?? ''}` : 'Nicht zugeordnet'}</span>
            <span className="wawi-leise">{zahl(o.artikel)} Artikel{o.keinBestand ? ` · ${zahl(o.keinBestand)} ohne Bestand` : ''}</span>
          </summary>
          <div className="wawi-tabelle-wrap">
            <table className="wawi-tabelle">
              <thead>
                <tr>
                  <th scope="col">Warengruppe</th>
                  <th scope="col" className="wawi-zahl">Artikel</th>
                  {spalten.map((f) => <th key={f} scope="col" className="wawi-zahl" title={`Summe Bestand ${filialLabel(f)}`}>Σ {filialKurz(f)}</th>)}
                </tr>
              </thead>
              <tbody>
                {o.gruppen.map((g) => (
                  <tr key={g.wgNr ?? '-'}>
                    <td data-label="Warengruppe">
                      <button type="button" className="wawi-link" onClick={() => zuArtikel(o.oberNr, g.wgNr)}>{g.wgNr ? `${g.wgNr} · ${g.wgName ?? ''}` : 'Nicht zugeordnet'}</button>
                    </td>
                    <td data-label="Artikel" className="wawi-zahl">{zahl(g.artikel)}{g.keinBestand ? <span className="wawi-leise"> ({zahl(g.keinBestand)} ohne)</span> : null}</td>
                    {spalten.map((f) => {
                      const a = g.filialen.find((x) => x.filialNr === f)!;
                      return (
                        <td key={f} data-label={`Σ ${filialLabel(f)}`} className="wawi-zahl" title={`${a.positiv} positiv · ${a.negativ} negativ · ${a.null} auf 0`}>
                          <BestandZelle text={zahl(a.summe)} negativ={a.summe < 0} />
                          {a.negativ > 0 && <span className="wawi-leise wawi-minus"> ({a.negativ}×−)</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="wawi-summe">
                  <td data-label="Summe">Summe</td>
                  <td data-label="Artikel" className="wawi-zahl">{zahl(o.artikel)}</td>
                  {spalten.map((f) => {
                    const a = o.filialen.find((x) => x.filialNr === f)!;
                    return <td key={f} data-label={`Σ ${filialLabel(f)}`} className="wawi-zahl"><BestandZelle text={zahl(a.summe)} negativ={a.summe < 0} /></td>;
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </details>
      ))}
    </div>
  );
}

function Sync() {
  const s = useLaden<WawiSync>(() => wawiApi.sync(), []);
  if (s.kind === 'laden') return <p className="wawi-hinweis" role="status">Lädt …</p>;
  if (s.kind === 'fehler') return <Fehler status={s.status} />;
  const d = s.daten;
  const phase = PHASE_LABEL[d.phase];
  return (
    <div className="wawi-seite">
      <div className="wawi-kacheln">
        <div className="pb-card wawi-kachel"><p className="wawi-kachel__label">Letzter erfolgreicher Lauf</p><p className="wawi-kachel__wert">{zeit(d.lastSuccessAt)}</p></div>
        <div className="pb-card wawi-kachel"><p className="wawi-kachel__label">Bestandsstand (NEO)</p><p className="wawi-kachel__wert">{zeit(d.snapshotAt)}</p>{d.veraltet && <span className="wawi-marke wawi-marke--warn">veraltet</span>}</div>
        <div className="pb-card wawi-kachel"><p className="wawi-kachel__label">Stamm-Cursor (deltaVersion)</p><p className="wawi-kachel__wert">{d.artikelCursor ?? '—'}</p></div>
        <div className="pb-card wawi-kachel"><p className="wawi-kachel__label">Geführte Artikel</p><p className="wawi-kachel__wert">{zahl(d.artikel)}</p><p className="wawi-hinweis">davon {zahl(d.keinBestand)} ohne Bestand</p></div>
      </div>
      <div className="pb-card wawi-karte">
        <h2 className="wawi-karte__titel">Lauf</h2>
        <dl className="wawi-daten">
          <dt>Status</dt><dd>{d.phase === 'fehler' || d.phase === 'unvollstaendig' ? <span className="wawi-marke wawi-marke--warn">{phase}</span> : phase}</dd>
          <dt>Lauf-ID</dt><dd>{d.runId ?? '—'}</dd>
          <dt>Lease bis</dt><dd>{zeit(d.lockedUntil)}</dd>
          <dt>Fortschritt</dt><dd>{d.checkpoint.bestandSeiten ?? 0} Bestandsseiten · Stammseite {d.checkpoint.artikelSeite ?? '—'}</dd>
          <dt>Abgelehnte Abschlüsse</dt><dd>{d.fehlerZaehler}</dd>
          <dt>Offene Kandidaten</dt><dd>{d.checkpoint.zurKlaerung?.length ? d.checkpoint.zurKlaerung.join(', ') : '—'}</dd>
          <dt>Letzter Fehler</dt><dd>{d.lastError ? <span className="wawi-fehler">{d.lastError} ({zeit(d.lastErrorAt)})</span> : '—'}</dd>
        </dl>
      </div>
    </div>
  );
}
